import { useEffect, useMemo, useRef } from "react";
import { formatToman, formatNumber, formatTomanInt } from "../utils/format";

export interface ActivityDay {
  date: string;
  cost: number;
}

export interface ActivityStats {
  longest_streak: number;
  avg_per_day: number;
  avg_per_week: number;
  total: number;
}

export interface ActivityData {
  days: ActivityDay[];
  stats: ActivityStats;
}

interface ActivityHeatmapProps {
  data?: ActivityData;
  loading?: boolean;
}

// GitHub-style contribution graph with 7 rows (شنبه..جمعه).
// Slightly smaller cell + buffered container so hover scaling never triggers a scrollbar.
const GAP = 3;
const MIN_CELL = 9;

const EMPTY_COLOR = "rgb(var(--bg-surface-hover))";
const LEVEL_COLORS = [
  "rgba(29, 156, 81, 0.15)",
  "rgba(29, 156, 81, 0.4)",
  "rgba(29, 156, 81, 0.65)",
  "rgba(29, 156, 81, 0.95)",
];

const ROW_LABELS = ["شنبه", "یکشنبه", "دوشنبه", "سه‌شنبه", "چهارشنبه", "پنجشنبه", "جمعه"];
// Rows with a visible weekday label (GitHub shows a few, not all).
const LABEL_ROWS = [0, 3, 6];

interface CellData {
  date: string;
  cost: number;
  level: number; // 0 = no activity, 1..4 = increasing intensity
}

function levelFor(cost: number, maxCost: number): number {
  if (cost <= 0) return 0;
  if (maxCost <= 0) return 1;
  const ratio = cost / maxCost;
  if (ratio > 0.75) return 4;
  if (ratio > 0.5) return 3;
  if (ratio > 0.25) return 2;
  return 1;
}

function parseDay(dateStr: string): Date {
  return new Date(`${dateStr}T00:00:00`);
}

function buildGrid(days: ActivityDay[]): { weeks: CellData[][]; maxCost: number } {
  if (!days.length) return { weeks: [], maxCost: 0 };

  let maxCost = 0;
  for (const d of days) maxCost = Math.max(maxCost, d.cost);

  // Row of the first day (0 = شنبه). `getDay()` is 0 (Sun)..6 (Sat).
  const firstDow = (parseDay(days[0].date).getDay() + 1) % 7;
  const totalSlots = days.length + firstDow;
  const weekCount = Math.ceil(totalSlots / 7);

  const weeks: CellData[][] = [];
  for (let w = 0; w < weekCount; w++) {
    const week: CellData[] = [];
    for (let row = 0; row < 7; row++) {
      const idx = w * 7 + row - firstDow;
      if (idx >= 0 && idx < days.length) {
        const d = days[idx];
        week.push({ date: d.date, cost: d.cost, level: levelFor(d.cost, maxCost) });
      } else {
        week.push(null!);
      }
    }
    weeks.push(week);
  }
  return { weeks, maxCost };
}

function buildMonthLabels(weeks: CellData[][]): string[] {
  const labels: string[] = weeks.map(() => "");
  let prev = "";
  weeks.forEach((week, col) => {
    const first = week.find(Boolean);
    if (!first) return;
    const label = parseDay(first.date).toLocaleString("fa-IR", { month: "short" });
    if (label !== prev) {
      labels[col] = label;
      prev = label;
    }
  });
  return labels;
}

export default function ActivityHeatmap({ data, loading }: ActivityHeatmapProps) {
  const { weeks, maxCost } = useMemo(() => buildGrid(data?.days || []), [data]);
  const monthLabels = useMemo(() => buildMonthLabels(weeks), [weeks]);
  const scrollRef = useRef<HTMLDivElement>(null);

  // On mobile the heatmap overflows horizontally. By default it shows the
  // oldest days (left); scroll it to the newest days (right) on load so the
  // most recent activity is visible immediately, and older days are reached
  // by sliding left.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || loading || !weeks.length) return;
    el.scrollTo({ left: el.scrollWidth, behavior: "instant" });
  }, [loading, weeks.length]);

  if (loading) {
    return <div className="h-52 w-full bg-surface-hover rounded animate-pulse" />;
  }

  if (!data?.days?.length) {
    return (
      <div className="h-52 flex items-center justify-center text-text-tertiary">
        <p className="text-body">داده‌ای برای نمایش وجود ندارد</p>
      </div>
    );
  }

  const stats = data.stats;

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="rounded-btn bg-surface-base p-3">
          <p className="text-small text-text-tertiary mb-1">طولانی‌ترین زنجیره</p>
          <p className="text-h2 text-text-main font-number">
            {formatNumber(stats?.longest_streak)}
            <span className="text-body text-text-tertiary font-normal mr-1">روز</span>
          </p>
        </div>
        <div className="rounded-btn bg-surface-base p-3">
          <p className="text-small text-text-tertiary mb-1">میانگین روزانه</p>
          <p className="text-h2 text-text-main font-number">
            {formatTomanInt(stats?.avg_per_day)}
            <span className="text-body text-text-tertiary font-normal mr-1">تومان</span>
          </p>
        </div>
        <div className="rounded-btn bg-surface-base p-3">
          <p className="text-small text-text-tertiary mb-1">میانگین هفتگی</p>
          <p className="text-h2 text-text-main font-number">
            {formatTomanInt(stats?.avg_per_week)}
            <span className="text-body text-text-tertiary font-normal mr-1">تومان</span>
          </p>
        </div>
        <div className="rounded-btn bg-surface-base p-3">
          <p className="text-small text-text-tertiary mb-1">مجموع</p>
          <p className="text-h2 text-text-main font-number">
            {formatTomanInt(stats?.total)}
            <span className="text-body text-text-tertiary font-normal mr-1">تومان</span>
          </p>
        </div>
      </div>

      {/* Heatmap */}
      <div
        dir="ltr"
        ref={scrollRef}
        className="overflow-x-auto overflow-y-hidden py-1 -my-1"
        style={{ scrollbarWidth: "thin", paddingInline: 4 }}
      >
        <div
          className="grid activity-heatmap-grid"
          style={{
            gridTemplateColumns: `auto repeat(${weeks.length}, minmax(${MIN_CELL}px, 1fr))`,
            columnGap: GAP,
            rowGap: GAP,
            // Shrink the grid a touch so the hover scale on the last column
            // has breathing room and does not extend the scroll width.
            maxWidth: "calc(100% - 6px)",
            paddingBlock: 2,
          }}
        >
          {/* Month labels (first row, after the empty weekday-label cell) */}
          <div style={{ gridColumn: 1, gridRow: 1, height: 14 }} />
          {monthLabels.map((label, col) => (
            <span
              key={col}
              className="text-[9px] text-text-tertiary leading-[14px] whitespace-nowrap"
              style={{ gridColumn: col + 2, gridRow: 1, textAlign: "left", height: 14 }}
            >
              {label}
            </span>
          ))}

          {/* Weekday labels (column 1) + cells, one grid row per weekday */}
          {ROW_LABELS.map((label, row) => (
            <div
              key={label}
              className="ah-row-label flex items-center justify-end text-[9px] text-text-tertiary whitespace-nowrap pr-1"
              style={{
                gridColumn: 1,
                gridRow: row + 2,
                visibility: LABEL_ROWS.includes(row) ? "visible" : "hidden",
              }}
            >
              {label}
            </div>
          ))}

          {weeks.map((week, wi) =>
            week.map((cell, ri) => {
              if (!cell) {
                return (
                  <div
                    key={`${wi}-${ri}`}
                    className="ah-cell rounded-[3px]"
                    style={{ gridColumn: wi + 2, gridRow: ri + 2, aspectRatio: "1 / 1", background: "transparent" }}
                  />
                );
              }
              return (
                <div
                  key={`${wi}-${ri}`}
                  title={`${parseDay(cell.date).toLocaleDateString("fa-IR")} — ${formatToman(cell.cost)} تومان`}
                  className="ah-cell rounded-[3px] transition-transform duration-150 ease-out hover:scale-110 hover:z-10 hover:shadow-sm"
                  style={{
                    gridColumn: wi + 2,
                    gridRow: ri + 2,
                    aspectRatio: "1 / 1",
                    background: cell.level === 0 ? EMPTY_COLOR : LEVEL_COLORS[cell.level - 1],
                    boxShadow: cell.cost > 0 && cell.level === 4 ? "0 0 0 1px rgba(29,156,81,0.4)" : undefined,
                    willChange: "transform",
                  }}
                />
              );
            }),
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-end gap-1.5 mt-3">
        <span className="text-[10px] text-text-tertiary ml-1">کمتر</span>
        <span className="rounded-[3px]" style={{ width: 11, height: 11, background: EMPTY_COLOR }} />
        {LEVEL_COLORS.map((c) => (
          <span key={c} className="rounded-[3px]" style={{ width: 11, height: 11, background: c }} />
        ))}
        <span className="text-[10px] text-text-tertiary mr-1">بیشتر</span>
      </div>
    </div>
  );
}
